import { PostureCell, Spinner } from "@constructorfleet/ui/design-system";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ProductDbClient } from "@constructorfleet/bhg-product-core";

type NextScheduledCallResponse = {
  ok: boolean;
  notificationTimeUtc?: string | null;
  notificationTimezone?: string | null;
  pendingCount?: number | null;
};

function formatUtcTime(date: Date) {
  return date.toISOString().slice(11, 19) + " UTC";
}

function formatLocalTime(date: Date) {
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

function formatLocalTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "Local";
  }
}

function buildNextCallDate(notificationTimeUtc: string): Date | null {
  const match = notificationTimeUtc.match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const now = new Date();

  const today = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hour, minute, 0, 0)
  );

  if (today.getTime() <= now.getTime()) {
    today.setUTCDate(today.getUTCDate() + 1);
  }

  return today;
}

function formatCountdown(diffMs: number) {
  if (diffMs <= 0) return "Now";

  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || hours > 0) parts.push(`${String(minutes).padStart(2, "0")}m`);
  parts.push(`${String(seconds).padStart(2, "0")}s`);

  return parts.join(" ");
}

export function NextCallCard({ dbClient }: { dbClient: ProductDbClient }) {
  const [now, setNow] = useState(() => new Date());
  const [nextCall, setNextCall] = useState<NextScheduledCallResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNextCall = useCallback(async () => {
    if (!dbClient.requestAdmin) {
      setLoading(false);
      return;
    }
    const result = await dbClient.requestAdmin<NextScheduledCallResponse>({
      method: "GET",
      path: "/api/admin/bhg-call/next-scheduled-call",
    });
    if (result.ok) {
      setNextCall(result.data);
    }
    setLoading(false);
  }, [dbClient]);

  useEffect(() => {
    intervalRef.current = setInterval(() => setNow(new Date()), 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    void fetchNextCall();
    const poll = setInterval(() => void fetchNextCall(), 60_000);
    return () => clearInterval(poll);
  }, [fetchNextCall]);

  const localTz = formatLocalTimezone();
  const nextCallTimeUtc = nextCall?.notificationTimeUtc ?? null;
  const nextCallDate = nextCallTimeUtc ? buildNextCallDate(nextCallTimeUtc) : null;
  const countdownMs = nextCallDate ? nextCallDate.getTime() - now.getTime() : null;
  const pendingCount = nextCall?.pendingCount ?? null;

  if (loading) {
    return <PostureCell label="Next call" value={<Spinner size="sm" label="Loading schedule" />} />;
  }

  const countdownStr =
    countdownMs != null && countdownMs > 0
      ? formatCountdown(countdownMs)
      : countdownMs != null
        ? "Now"
        : "—";

  const dot: "ok" | "mute" = countdownMs != null && countdownMs <= 0 ? "ok" : "mute";

  const sub = nextCallDate
    ? `${nextCallTimeUtc} UTC / ${formatLocalTime(nextCallDate)} ${localTz}`
    : "No pending calls today";

  return (
    <PostureCell
      label={
        <>
          Next call
          {pendingCount != null && pendingCount > 0 ? ` · ${pendingCount} pending` : ""}
        </>
      }
      value={countdownStr}
      dot={dot}
      sub={sub}
      monoSub
    />
  );
}
